import { useState, useEffect } from 'react';
import { signedUrlService } from '../services/signedUrlService';

interface UseSignedUrlOptions {
  enabled?: boolean;
  fallbackUrl?: string;
}

export function useSignedUrl(
  s3Key: string | null, 
  type: 'thumbnail' | 'full',
  options: UseSignedUrlOptions = {}
) {
  const { enabled = true, fallbackUrl } = options;
  const [url, setUrl] = useState<string | null>(fallbackUrl || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!s3Key || !enabled) {
      setUrl(fallbackUrl || null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    signedUrlService.getSignedUrl(s3Key, type)
      .then(signedUrl => {
        if (!cancelled) {
          setUrl(signedUrl);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err);
          setLoading(false);
          // Keep fallback URL if available
          if (fallbackUrl) {
            setUrl(fallbackUrl);
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [s3Key, type, enabled, fallbackUrl]);

  return { url, loading, error };
}

export function useSignedUrls(
  items: Array<{ s3Key: string }>,
  type: 'thumbnail' | 'full',
  options: UseSignedUrlOptions = {}
) {
  const { enabled = true } = options;
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!items.length || !enabled) {
      setUrls(new Map());
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const loadUrls = async () => {
      try {
        const urlMap = new Map<string, string>();
        
        // Load URLs in parallel
        const promises = items.map(async (item) => {
          try {
            const url = await signedUrlService.getSignedUrl(item.s3Key, type);
            if (!cancelled) {
              urlMap.set(item.s3Key, url);
            }
          } catch (err) {
            console.warn(`Failed to load signed URL for ${item.s3Key}:`, err);
          }
        });

        await Promise.allSettled(promises);
        
        if (!cancelled) {
          setUrls(urlMap);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err as Error);
          setLoading(false);
        }
      }
    };

    loadUrls();

    return () => {
      cancelled = true;
    };
  }, [items, type, enabled]);

  return { urls, loading, error };
}